import { personName } from "@/components/boo/access-copy";
import { BooAvatars, BooPair, DepthTag, ExposureNote, ScopeChips } from "@/components/grants/GrantBits";
import { ScopeEditor } from "@/components/grants/ScopeEditor";
import { Block, NoteField, OverrideReason, REASON_MIN, SheetShell } from "@/components/grants/SheetShell";
import { type GrantActor, useInvalidateLink, useSourceGrantable } from "@/components/grants/useGrantActions";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { InlineError } from "@/components/QueryState";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { api, type BooLinkRow, type GrantScopes } from "@/lib/api";
import { countIn, countLine, expirySoon, intersect, isEmpty, labelsOf, shortDate } from "@/lib/grant-scopes";
import { useMutation } from "@tanstack/react-query";
import { Quote } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// The consent screen: what the asking Boo wants, item by item, all checked to
// start. The source's admins uncheck what they won't share and approve the
// rest, or decline. Works for the first request and for a request for more.
export function ReviewRequestSheet({ actor, link, onClose }: { actor: GrantActor; link: BooLinkRow; onClose: () => void }) {
  const more = link.status === "approved" ? link.more : null;
  const asked = more ? more.scopes : link.requested;
  const source = link.source?.name ?? "this Boo";
  const consumer = link.consumer?.name ?? "the other Boo";
  const grantable = useSourceGrantable(actor, link);
  const [value, setValue] = useState<GrantScopes>(asked);
  const [reshare, setReshare] = useState(link.reshare);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [declineNote, setDeclineNote] = useState("");
  const invalidate = useInvalidateLink();
  const org = actor.kind === "org";
  const reasonOk = !org || reason.trim().length >= REASON_MIN;
  const chosen = intersect(value, asked);

  const decide = useMutation({
    mutationFn: (decision: "approve" | "decline") => {
      const body = { scopes: chosen, reshare, note: (decision === "decline" ? declineNote : note).trim() || null };
      if (org) return api.access.decide(link.id, decision === "approve" ? { decision, ...body, reason: reason.trim() } : { decision, note: body.note, reason: reason.trim() });
      return decision === "approve"
        ? api.containers.connections.approve(actor.containerId, link.id, body)
        : api.containers.connections.decline(actor.containerId, link.id, { note: body.note });
    },
    onSuccess: (_res, decision) => {
      toast.success(decision === "approve" ? `${consumer} can now learn what you approved from ${source}.` : `Declined. The admins of ${consumer} will be told.`);
      onClose();
    },
    onSettled: () => invalidate(link),
  });

  const g = grantable.data?.grantable;
  const counts = g ? countIn(chosen, g) : null;
  const asker = more ? more.requestedBy : link.requestedBy;
  const askNote = more ? more.note : link.requestNote;
  const askedAt = more ? more.requestedAt : link.requestedAt;

  return (
    <SheetShell
      title={more ? `${consumer} is asking for more` : `${consumer} wants to learn from ${source}`}
      onClose={onClose}
      busy={decide.isPending}
      footer={
        <>
          <Button variant="outline" onClick={() => {
              // The sheet's note goes with either decision: start the reason from it.
              if (!declineNote.trim()) setDeclineNote(note);
              setDeclining(true);
            }} disabled={decide.isPending || !reasonOk}>
            Decline
          </Button>
          <Button onClick={() => decide.mutate("approve")} loading={decide.isPending && decide.variables === "approve"} disabled={decide.isPending || isEmpty(chosen) || !reasonOk}>
            {counts && g ? `Approve · ${countLine(counts, countIn(asked, g))}` : "Approve"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
        {more ? <BooPair consumer={link.consumer} source={link.source} /> : <BooAvatars consumer={link.consumer} source={link.source} />}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>
            Asked by {personName(asker)}
            {askedAt ? ` · ${shortDate(askedAt)}` : ""}
          </span>
          {expirySoon(link.expiresAt) && <span className="text-amber-800 dark:text-amber-300">{expirySoon(link.expiresAt)}</span>}
          {!more && <DepthTag depth={link.requestedPreset} />}
        </div>
        {askNote && (
          <p className="flex gap-2 rounded-md bg-muted/60 px-3 py-2 text-sm">
            <Quote className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span>{askNote}</span>
          </p>
        )}
        {more && link.granted && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>Already shared:</span>
            <ScopeChips scopes={link.granted} labels={labelsOf(g)} grantable={g} />
          </div>
        )}
      </div>

      <Block title={more ? "Also asking for" : "Asking for"}>
        {grantable.isLoading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        ) : grantable.isError ? (
          <InlineError error={grantable.error} />
        ) : g ? (
          <>
            <ScopeEditor grantable={g} bound={asked} value={value} onChange={setValue} showTitles={!org} skillsAllOrNone={grantable.data?.scoped === false} disabled={decide.isPending} />
            {grantable.data && !grantable.data.scoped && (
              <p className="text-xs text-muted-foreground">
                {source} is on an older version: it can share all of its skills or none, and channels and one-on-ones need an update.
              </p>
            )}
          </>
        ) : null}
      </Block>

      <ExposureNote consumer={consumer} source={source} scopes={chosen} grantable={g} />

      <label className="flex cursor-pointer items-start justify-between gap-4 rounded-lg border border-border px-4 py-3">
        <span>
          <span className="block text-sm font-medium [overflow-wrap:anywhere]">Allow re-sharing</span>
          <span className="block text-xs text-muted-foreground">
            <span className="[overflow-wrap:anywhere]">Boos that learn from {consumer} can get these memories too.</span>
          </span>
        </span>
        <Switch checked={reshare} onCheckedChange={setReshare} disabled={decide.isPending} aria-label="Allow re-sharing" />
      </label>

      <NoteField id="decision-note" label="Note (optional)" value={note} onChange={setNote} />
      {org && <OverrideReason value={reason} onChange={setReason} boo={link.source?.name} />}
      {decide.isError && <InlineError error={decide.error} />}

      <ConfirmDialog
        open={declining}
        onOpenChange={setDeclining}
        title={more ? "Decline the request for more?" : "Decline this request?"}
        description={more ? `${consumer} keeps what it already learns from ${source}.` : `${consumer} won't learn from ${source}.`}
        confirmLabel="Decline"
        loading={decide.isPending}
        onConfirm={() => decide.mutate("decline")}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="decline-note" className="text-xs text-muted-foreground [overflow-wrap:anywhere]">
            Reason (optional), shown to the admins of {consumer}
          </Label>
          <Textarea id="decline-note" value={declineNote} maxLength={500} rows={2} onChange={(e) => setDeclineNote(e.target.value)} />
        </div>
      </ConfirmDialog>
    </SheetShell>
  );
}
